import { Dispatch, FC, Fragment, SetStateAction, useCallback, useEffect, useState } from 'react';
import {
  DragDropContext,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
} from 'react-beautiful-dnd';
import { COLORS, SPACINGS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import ConditionalConfiguration from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/ConditionalConfiguration';
import EditConditionalPanel from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/EditConditionalPanel';
import ElseSection from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/ElseSection';
import HoverableDraggable from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/HoverableDraggable';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { reorder } from 'dashboard/lib/dnd/utils';
import { UsableObjectConfig } from 'dashboard/lib/workflows';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError, WorkflowSchema } from 'dashboard/models/workflow';
import { WorkflowRunStepConditional } from 'dashboard/models/workflow-step';
import {
  newPlaceholderStep,
  newWorkflowConditional,
  WorkflowConditional,
  WorkflowElse,
  WorkflowStep,
  WorkflowStepConditional,
} from 'dashboard/models/workflow-version';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { JsonSchema } from 'lib/json-schema';
import { buildGraph } from 'lib/workflows';

const RouteList = styled.div<{ isDraggingOver: boolean; removeMargin: boolean }>`
  ${({ isDraggingOver, removeMargin }) => css`
    margin-left: ${removeMargin ? SPACINGS.base * -4 : 0}px;
    margin-right: ${removeMargin ? SPACINGS.base * -4 : 0}px;
    background-color: ${isDraggingOver ? COLORS.indigo10 : COLORS.white};
  `};
  transition:
    background-color 0.4s ease-in-out,
    box-shadow 0.4s ease-in-out;
`;

const ItemContainer = styled.section`
  flex: 1;
`;

interface StepConfigConditionalProps {
  conditionalRouteToEdit: number | null;
  editable: boolean;
  errors: WorkflowError[];
  jsonSchema: JsonSchema;
  step: WorkflowStepConditional;
  stepsConfig: WorkflowStep[];
  tokensToDisplayValue: Record<string, string>;
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  workflowRunStep: WorkflowRunStepConditional;
  workflowSchema: WorkflowSchema;
  setConditionalRouteToEdit: Dispatch<SetStateAction<number>>;
  updateStep: (node: WorkflowStep) => void;
}

const StepConfigConditional: FC<StepConfigConditionalProps> = ({
  conditionalRouteToEdit,
  editable,
  errors,
  jsonSchema,
  step,
  stepsConfig,
  tokensToDisplayValue,
  trigger,
  usableObjects,
  workflowRunStep,
  workflowSchema,
  setConditionalRouteToEdit,
  updateStep,
}) => {
  const { conditionals, branches } = step;
  const [dndConditionals, setDndConditionals] = useState(conditionals);
  const tokensToSteps = buildGraph(stepsConfig);

  useEffect(() => {
    setDndConditionals(conditionals);
  }, [conditionals]);

  const updateConditionalAtIndex = useCallback(
    (newConditional: WorkflowConditional, index: number) => {
      updateStep({
        ...step,
        conditionals: [
          ...conditionals.slice(0, index),
          newConditional,
          ...conditionals.slice(index + 1),
        ],
      });
    },
    [conditionals, step, updateStep],
  );

  const handleAddNewRoute = useCallback(() => {
    updateStep({
      ...step,
      conditionals: [
        ...conditionals.slice(0, conditionals.length - 1),
        newWorkflowConditional(''),
        conditionals[conditionals.length - 1],
      ],
      branches: [
        ...branches.slice(0, branches.length - 1),
        [newPlaceholderStep()],
        branches[branches.length - 1],
      ],
    });
  }, [updateStep, step]);

  const deleteRoute = useCallback(
    (conditionalIndex: number) => () => {
      const newStep: WorkflowStepConditional = {
        ...step,
        branches: [...branches.slice(0, conditionalIndex), ...branches.slice(conditionalIndex + 1)],
        conditionals: [
          ...conditionals.slice(0, conditionalIndex),
          ...conditionals.slice(conditionalIndex + 1),
        ],
      };

      updateStep(newStep);
    },
    [step, updateStep],
  );

  const handleDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const reorderedConditionals = reorder(
      conditionals,
      result.source.index,
      result.destination.index,
    );
    const reorderedBranches = reorder(branches, result.source.index, result.destination.index);
    // optimistically update UI
    setDndConditionals(reorderedConditionals);
    // update redux
    updateStep({
      ...step,
      conditionals: reorderedConditionals,
      branches: reorderedBranches,
    });
  };

  const lastIndex = dndConditionals.length - 1;
  const renderRouteSections = (
    droppableProvided: DroppableProvided,
    droppableSnapshot: DroppableStateSnapshot,
  ) => (
    <RouteList
      ref={droppableProvided.innerRef}
      isDraggingOver={droppableSnapshot.isDraggingOver}
      removeMargin={editable}
    >
      {dndConditionals.map((conditional, index) => {
        if (index === lastIndex) {
          return null;
        }

        const routeNumber = index + 1;
        return (
          <Fragment key={conditional.token}>
            <HoverableDraggable
              draggableId={conditional.token}
              index={index}
              isDragDisabled={!editable}
            >
              <ItemContainer data-test={`step-config-conditional__route-item-container-${index}`}>
                <ConditionalConfiguration
                  editable={editable}
                  errors={errors}
                  conditional={conditional as WorkflowConditional}
                  tokensToSteps={tokensToSteps}
                  jsonSchema={jsonSchema}
                  tokensToDisplayValue={tokensToDisplayValue}
                  usableObjects={usableObjects}
                  routeIndex={index}
                  headingText={`Route ${index + 1}`}
                  workflowRunStep={workflowRunStep}
                  deleteRoute={deleteRoute(index)}
                  onEditClick={() => setConditionalRouteToEdit(index + 1)}
                />
              </ItemContainer>
            </HoverableDraggable>
            {editable && conditionalRouteToEdit === routeNumber && (
              <EditConditionalPanel
                conditional={conditional as WorkflowConditional}
                panelTitle={`Edit Route ${routeNumber}`}
                workflowSchema={workflowSchema}
                jsonSchema={jsonSchema}
                tokensToDisplayValue={tokensToDisplayValue}
                allowEditName
                updateConditional={(newConditional) => {
                  updateConditionalAtIndex(newConditional, index);
                }}
                onClose={() => {
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  setConditionalRouteToEdit(null);
                }}
              />
            )}
          </Fragment>
        );
      })}
      {droppableProvided.placeholder}
    </RouteList>
  );

  const { renderErrorMessageFor } = useWorkflowError<'conditionals' | 'branches'>(errors);

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='droppable'>{renderRouteSections}</Droppable>
      </DragDropContext>
      <ElseSection
        conditional={dndConditionals[lastIndex] as WorkflowElse}
        errors={errors}
        index={lastIndex}
        key={lastIndex}
        workflowRunStep={workflowRunStep}
      />
      {editable && (
        <>
          <Spacer height={2} />
          <div>
            <Button
              id='step-config-conditional__add-new-route'
              data-test='step-config-conditional__add-new-route'
              type='button'
              variant='basic'
              width='auto'
              onClick={handleAddNewRoute}
            >
              Add New Route
            </Button>
          </div>
          {renderErrorMessageFor(['conditionals', 'branches'])}
          <Spacer height={2} />
          <StepAdvancedConfigurationWithErrorHandlingPanel
            step={step}
            trigger={trigger}
            updateStep={updateStep}
          />
        </>
      )}
    </>
  );
};

export default StepConfigConditional;
